import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

const Container = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
`;

const Content = styled.div`
  text-align: center;
  position: relative;
  padding: 2rem;
  max-width: 800px;
`;

const Kicker = styled(motion.span)`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: clamp(0.75rem, 1.5vw, 0.875rem);
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: 3px;
  text-transform: uppercase;
  display: inline-block;
  margin-bottom: 2rem;

  &::before {
    content: '//';
    margin-right: 0.5rem;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const WelcomeText = styled(motion.h1)`
  font-family: 'Crimson Pro', 'Georgia', serif;
  font-size: clamp(2.5rem, 9vw, 5.5rem);
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.2;
`;

const AccentText = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  position: relative;
`;

const Tagline = styled(motion.p)`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: clamp(1rem, 2.5vw, 1.25rem);
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 1.5rem 0 0 0;
  font-weight: 400;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

interface IntroProps {
  onComplete: () => void;
}

const welcomeMessages = [
  'There might be some connection between us',
  "Plot twist: I'm not a robot...",
  'Warning: May contain traces of creativity and coffee',
  'Powered by late-night coding sessions',
  'Fun fact: I once debugged for 6 hours...',
  'Professional pixel pusher & bug whisperer',
  'Turning caffeine into code since forever',
  'I speak fluent React, TypeScript, and sarcasm',
  'Error 404: Social life not found',
  'Building apps and breaking stereotypes',
  "Commit message: 'It works, don't touch it'",
  "My code doesn't always work, but when it does...",
  "Debugging: Being the detective in a crime movie where you're also...",
  "I'm not procrastinating, I'm doing asynchronous work",
  "Production? I thought you said 'production-ready adjacent'",
  "Git commit -m 'fixed stuff'",
  'Console.log() is my therapist',
  'Sleep is just a really long loading screen',
  'Stackoverflow: Copy. Paste. Pray.',
  "My relationship status: It's complicated...",
  "I don't always test my code, but when I do...",
  "Senior dev: I don't remember writing this...",
  'In my defense, the code was working on my machine',
  'I have a joke about UDP, but you might not...',
  'There are 10 types of people: those who understand binary and...',
  'I would tell you a joke about null, but...',
  'AJAX: Asynchronous JavaScript And...',
  "The cloud is just someone else's computer that can also...",
  "Semicolons: The difference between 'I love programming' and...",
  'Tried to catch some fog earlier...',
  "My code is self-documenting. That's why nobody...",
  "Hofstadter's Law: It always takes longer than...",
  'Legacy code: Code that works, and nobody knows...',
  'Programming is 10% writing code and 90%...',
  'CSS: Cascading Style Suffering',
  "Prettier formatted my code. Now it's pretty...",
  'My weekend plans: Netflix, chill, and...',
  'Inheritance in real life: Money. Inheritance in code...',
  'Race condition: When your code has trust issues with...',
  'Callback hell is where all my best ideas go to...',
  'Documentation: The theory. Reality...',
  "Technical debt: When 'later' finally...",
  'Rubber duck debugging: Cheaper than...',
  'The best error message: Something went wrong...',
  "Agile: Where we pretend to know what we're doing, but...",
  'Two hard things in CS: Naming things, cache invalidation, and...',
  'Started learning recursion. To understand it, first...',
  'My password is the last 8 digits of...',
  'Code review: Where friends become...',
  'PM: Is it done? Dev: Just a few small bugs...',
  'Deadlines are just suggestions with...',
  'Copied from Stackoverflow and still got...',
  'Working 8 hours a day: 7 hours meetings, 1 hour...',
  'Boss: Ship the new feature. Dev: But the old bugs... Boss...',
  'The worst part about being a dev: Finishing code, then...',
  'Love is like an API: Sometimes it works, sometimes...',
  'Code late at night, debug in the morning, fix bugs...',
  'Going to work is like going to a funeral: leave in the morning, return...',
  'Family at reunions: You work in IT? Fix my computer...',
  'Developer salary: High pay, short...',
  'I dreamed about you last night... in a...',
  // 50 NEW ADDITIONS
  "I don't need a debugger, I need a rubber duck and a vacation",
  'My code compiles on the first try... said no one ever',
  'Refactoring: An excuse to rewrite code that already works',
  "There's no place like 127.0.0.1",
  "I'm not lazy, I'm just highly motivated to do nothing efficiently",
  'Will code for equity and an unbothered calendar',
  "Algorithm: A word used by programmers when they don't want to explain",
  'Hardware: The part of a computer that you can kick',
  'Software: The part of a computer that you can only curse at',
  "I'm thinking, please wait...",
  'Loading enthusiasm for the next stand-up...',
  'Installing coffee dependencies...',
  'Optimizing the optimizer...',
  'Reticulating splines...',
  'Compiling plausible excuses...',
  'Downloading more RAM...',
  'Blinking frantically behind the terminal...',
  'Generating witty loading message. Please hold...',
  'Please wait while the elves draw your screen',
  'Testing your patience since 1999',
  'Counting backwards from Infinity...',
  'My favorite data structure is a pizza box',
  'O(1) in the streets, O(n!) in the sheets',
  "I put the 'API' in 'hAPPIness'",
  'Deploying to production on a Friday. Pray for me.',
  "I don't have bugs, I have spontaneous undocumented features",
  'A SQL query goes into a bar, walks up to two tables and asks...',
  "Why do Java programmers have to wear glasses? Because they don't C#",
  "My keyboard is missing the F5 key, it's a very refreshing setup",
  "I'm not a control freak, I just require root access",
  "I'm multitasking: wasting time, being unproductive, and procrastinating",
  "It's not a memory leak, it's an expansive digital footprint",
  "I am fluent in 6,000 forms of communication, but can't talk to the backend",
  'I like my coffee like my code: Dark, bitter, and full of bugs',
  'Hello, World. We meet again.',
  "I'll be back... right after this loading screen finishes.",
  'May the Source Code be with you',
  "I'm a programmer; I don't solve problems, I create elegantly automated ones",
  'I write code that works 60% of the time, every time',
  "My code works perfectly, I just don't know why",
  "I don't fix bugs, I 'optimize legacy behavior'",
  'I need a <br> right now',
  "I'm entirely <div>-inely inspired today",
  'Catch you later, Promise!',
  'Awaiting your response... timeout in 30ms.',
  'Fetching inspiration from the cloud...',
  'Unpacking the mysteries of CSS...',
  'Silently judging your browser choices...',
  "Locating the 'Any' key...",
  'Calibrating the flux capacitor...',
];

const Intro: React.FC<IntroProps> = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [randomMessage] = useState(
    () => welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onComplete, 800);
    }, 4000);

    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <Container
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{
            opacity: 0,
            y: -20,
          }}
          transition={{
            duration: 0.8,
            ease: [0.43, 0.13, 0.23, 0.96],
          }}
        >
          <Content>
            <Kicker
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.2,
                ease: 'easeOut',
              }}
            >
              Hello There
            </Kicker>

            <WelcomeText
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.4,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              Welcome to My <AccentText>Portfolio</AccentText>
            </WelcomeText>

            <Tagline
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.8,
                ease: 'easeOut',
              }}
            >
              {randomMessage}
            </Tagline>
          </Content>
        </Container>
      )}
    </AnimatePresence>
  );
};

export default Intro;
